<script setup>
import {
  ref,
  computed,
  onMounted,
  onBeforeUnmount,
  watch,
  nextTick,
} from 'vue';
import { useI18n } from 'vue-i18n';
import Draggable from 'vuedraggable';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import ToggleSwitch from 'dashboard/components-next/switch/Switch.vue';
import { useNumberFormatter } from 'shared/composables/useNumberFormatter';
import { resolveActiveIcon } from 'dashboard/helper/activeIcon';
import { resolveSavedIconClass } from 'dashboard/helper/legacyIconClass';
import {
  ADMIN_ASSIGNEE_SCOPE,
  ADMIN_ASSIGNEE_SCOPE_VALUES,
  normalizeIncludeUnassignedInPendentes,
  PRIMARY_SEGMENT_KEYS,
  QUICK_FILTERS,
  isKanbanPresetKey,
  isDuplicableKanbanPresetKey,
  isFolderFilterKey,
  folderIdFromKey,
  createKanbanPreset,
  duplicateKanbanPreset,
  normalizeKanbanPreset,
} from './conversation/conversationQuickFilters.js';
import { CONVERSATION_COLUMN_INSET_CLASS } from '../conversationColumnLayout';

// SiteUp: barra de filtros rápidos da lista de conversas.
// v5: (1) segmento Não respondidas · Minhas · Todas
//     (2) ícones de atalho dos filtros que o atendente fixou
//     (3) painel Atalhos: ordenação + grupos WA + escopo admin + criar filtro (sem builder Kanban)
// Campanha e canais sociais NÃO ficam aqui — a aba Automação isola esses casos.
// Funil/etapa Kanban: seletor sob a barra quando um atalho Kanban está ativo
// (persistido em conversations_quick_filters.kanbanFunnel/kanbanStage).
const props = defineProps({
  items: {
    type: Array,
    default: () => [],
  },
  allItems: {
    type: Array,
    default: () => [],
  },
  activeKey: {
    type: String,
    default: '',
  },
  // Por padrão a barra é só-ícones (o nome aparece no tooltip ao passar o mouse).
  showLabels: {
    type: Boolean,
    default: false,
  },
  dense: {
    type: Boolean,
    default: false,
  },
  // Mapa key → i-lucide-* (ícones legados; hoje só exibidos, sem editor).
  iconOverrides: {
    type: Object,
    default: () => ({}),
  },
  // Mapa key → status (legado; hoje sem editor por-filtro).
  statusOverrides: {
    type: Object,
    default: () => ({}),
  },
  // SiteUp: caixa(s) de entrada GLOBAL — array de inbox_ids que vale pra TODOS
  // os filtros. Vazio = todas as caixas. Substitui o antigo mapa por-filtro.
  globalInbox: {
    type: Array,
    default: () => [],
  },
  // SiteUp: caixas de entrada disponíveis no seletor — já são as PERMITIDAS ao
  // usuário (o store getInboxes filtra por acesso; admin vê todas).
  inboxes: {
    type: Array,
    default: () => [],
  },
  // SiteUp: funis (com stages) pro seletor de funil/etapa do filtro Kanban.
  funnels: {
    type: Array,
    default: () => [],
  },
  // SiteUp: funil/etapa escolhidos pro filtro Kanban built-in ('' = todos).
  kanbanFunnel: {
    type: [String, Number],
    default: '',
  },
  kanbanStage: {
    type: String,
    default: '',
  },
  // SiteUp v654: presets Kanban duplicáveis persistidos por usuário.
  kanbanPresets: {
    type: Array,
    default: () => [],
  },
  // SiteUp: modo compacto da lista (legado; preservado, sem editor).
  compact: {
    type: Boolean,
    default: false,
  },
  // SiteUp: escopo global exclusivo para administradores.
  adminAssigneeScope: {
    type: String,
    default: ADMIN_ASSIGNEE_SCOPE.ALL,
  },
  // SiteUp: Atalhos — incluir fila não atribuída no chip Pendentes (default ON).
  includeUnassignedInPendentes: {
    type: Boolean,
    default: true,
  },
  canConfigureAdminAssigneeScope: {
    type: Boolean,
    default: false,
  },
  // SiteUp: filtro de grupos WhatsApp (all | hide | only).
  groupFilter: {
    type: String,
    default: 'all',
  },
  // SiteUp v647: sub-rail vertical (~56px) na coluna de conversas.
  orientation: {
    type: String,
    default: 'horizontal',
    validator: value => ['horizontal', 'vertical'].includes(value),
  },
  // SiteUp: alvo interno da coluna (nunca body / sidebar global).
  teleportTarget: {
    type: String,
    default: '#conversation-quick-filter-customizer-target',
  },
  // SiteUp: o botão Filtros do header chama openCustomizer(); no rail fica off.
  showCustomizeButton: {
    type: Boolean,
    default: true,
  },
  // SiteUp: sort lives in Atalhos (+) — mais recentes vs aguardando mais tempo.
  // SiteUp: no modo compacto o seletor de segmento migrou pro ChatListHeader
  // (ao lado da busca). A faixa de abas some, mas o componente segue montado
  // porque os painéis Filtro/Atalhos (toggleFilterPanel/toggleMorePanel) e o
  // chip de filtro aplicado continuam vivendo aqui.
  hideTabsRow: { type: Boolean, default: false },
});

const emit = defineEmits([
  'select',
  'updateConfig',
  'createFilter',
  'editCustomFilter',
  'deleteCustomFilter',
  'update:groupFilter',
]);

const isVertical = computed(() => props.orientation === 'vertical');

const { t } = useI18n();
const { formatCompactNumber } = useNumberFormatter();

const isCustomizing = ref(false);
const isMoreOpen = ref(false);
const morePanelRef = ref(null);
const moreTriggerRef = ref(null);
const morePanelStyle = ref({});
// D1 (header V3): FILTRAR é um controle próprio, separado do "+". O funil
// abre o menu de filtros (destinos + caixa); o "+" fica com atalhos/configs.
const isFilterOpen = ref(false);
const filterPanelRef = ref(null);
const filterTriggerRef = ref(null);
const filterPanelStyle = ref({});

const groupVisible = computed(() => props.groupFilter !== 'hide');

const toggleGroupVisibility = () => {
  emit('update:groupFilter', groupVisible.value ? 'hide' : 'all');
};

const draftItems = ref([]);
// Legados preservados no config (sem editor na UI): ícones, status, showLabels,
// compact. São carregados das props e reemitidos como estão.
const draftIcons = ref({});
const draftShowLabels = ref(false);
const draftCompact = ref(false);
const draftStatuses = ref({});
// SiteUp: caixa de entrada GLOBAL em edição (array de inbox_ids).
const draftGlobalInbox = ref([]);
// SiteUp: funil/etapa do filtro Kanban built-in em edição ('' = todos).
const draftKanbanFunnel = ref('');
const draftKanbanStage = ref('');
// SiteUp v654: cópia editável dos presets Kanban duplicáveis.
const draftKanbanPresets = ref([]);
const draftAdminAssigneeScope = ref(ADMIN_ASSIGNEE_SCOPE.ALL);
const draftIncludeUnassignedInPendentes = ref(true);

// Concept C: cartões-resumo colapsados por padrão quando o personalizador abre.
const isInboxExpanded = ref(false);
const isAdminExpanded = ref(false);
const isPendentesQueueExpanded = ref(false);

const labelFor = item =>
  item.name || t(`CHAT_LIST.QUICK_FILTERS.${item.i18nKey}`);

// SiteUp v6: segmento primário sempre com rótulos (Não lidas · Pendentes · Minhas).
const primaryKeySet = new Set(PRIMARY_SEGMENT_KEYS);
const segmentItems = computed(() => {
  const byKey = new Map(
    (props.allItems?.length ? props.allItems : props.items).map(item => [
      item.key,
      item,
    ])
  );
  return PRIMARY_SEGMENT_KEYS.map(key => {
    const item = byKey.get(key);
    if (item) return item;
    const preset = QUICK_FILTERS.find(entry => entry.key === key);
    return {
      key,
      i18nKey: preset?.i18nKey || key.toUpperCase(),
      icon: 'i-ph-funnel',
      count: 0,
    };
  });
});

// Coluna estreita mostra ícone; alargando, os rótulos voltam. `dense` já era o
// sinal de "lista compacta" que o ChatList manda, e agora ele acompanha a
// largura escolhida no arrasto em vez de ficar preso no Modo Foco.
const iconOnlySegment = computed(() => props.dense && !props.showLabels);

// O catálogo já traz ícone das três primárias (mail / time / user); o fallback
// cobre o item sintético montado aqui quando a chave não veio do catálogo.
const segmentIcon = item =>
  resolveActiveIcon(
    resolveSavedIconClass(item.icon || 'i-ph-funnel'),
    item.key === props.activeKey
  );

// Linha 2: só os filtros que o atendente deixou ativos (não ocultos).
const activeChipItems = computed(() =>
  (props.items || []).filter(item => !primaryKeySet.has(item.key))
);

// Estilo X: o atalho selecionado usa o ícone preenchido. Mesmo sinal
// (`item.key === activeKey`) que já pinta o chip de azul. Atalho sem par
// preenchido no catálogo (ex.: `i-ph-kanban`) fica no contorno.
// F1.2e: `resolveSavedIconClass` antes do `-fill` porque o ícone pode ter sido
// escolhido antes da migração pra Remix (ui_settings guarda `i-lucide-*`).
// Os itens que vêm do ChatList já chegam traduzidos; traduzir de novo é
// idempotente e cobre os itens montados aqui dentro (preset duplicado).
const chipIcon = item =>
  resolveActiveIcon(
    resolveSavedIconClass(item.icon),
    item.key === props.activeKey
  );

const isPrimaryActive = computed(() => primaryKeySet.has(props.activeKey));
const secondaryFilterActive = computed(
  () => !isPrimaryActive.value && !!props.activeKey
);

// V3 travada (2026-08-09/10): a fileira de ícones morreu — os destinos vivem no
// menu do "+". Com os chips fora da tela, o filtro secundário ativo precisa de
// um sinal próprio na lista: uma pílula sob as tabs, com × que volta pra última
// tab primária em que o atendente estava (não pra uma fixa).
const lastPrimaryKey = ref(PRIMARY_SEGMENT_KEYS[0]);
const segmentTabEls = new Map();

const setSegmentTabRef = (key, el) => {
  if (el) segmentTabEls.set(key, el);
  else segmentTabEls.delete(key);
};

const scrollActiveSegmentIntoView = () => {
  const el = segmentTabEls.get(props.activeKey);
  if (!el || typeof el.scrollIntoView !== 'function') return;
  el.scrollIntoView({ block: 'nearest', inline: 'nearest' });
};

watch(
  () => props.activeKey,
  key => {
    if (primaryKeySet.has(key)) lastPrimaryKey.value = key;
    nextTick(scrollActiveSegmentIntoView);
  },
  { immediate: true }
);

const activeSecondaryItem = computed(() => {
  if (!secondaryFilterActive.value) return null;
  const source = props.allItems?.length ? props.allItems : props.items;
  return (source || []).find(item => item.key === props.activeKey) || null;
});

const clearSecondaryFilter = () => {
  emit('select', lastPrimaryKey.value);
};

const showActiveKanbanScope = computed(() =>
  isKanbanPresetKey(props.activeKey)
);

// Espaço mínimo que vale a pena abrir pra baixo; abaixo disso o menu vira
// pra cima em vez de vazar pela borda inferior.
const MIN_SPACE_BELOW = 200;
const PANEL_GAP = 6;
const VIEWPORT_MARGIN = 12;

// Posicionamento comum aos dois menus do cromo (funil e "+").
function computePanelStyle(trigger) {
  if (!trigger) return {};
  const rect = trigger.getBoundingClientRect();
  // Largura sem piso cego: em viewport estreita o piso de 300px fazia o painel
  // ficar maior que a área útil (achado do review do PR #354).
  const width = Math.min(420, window.innerWidth - VIEWPORT_MARGIN * 2);
  let left = rect.right - width;
  left = Math.max(
    VIEWPORT_MARGIN,
    Math.min(left, window.innerWidth - width - VIEWPORT_MARGIN)
  );

  const spaceBelow = window.innerHeight - rect.bottom - VIEWPORT_MARGIN;
  const spaceAbove = rect.top - VIEWPORT_MARGIN;
  // Vira pra cima só se lá couber mais — senão fica embaixo com a altura real.
  const flipUp = spaceBelow < MIN_SPACE_BELOW && spaceAbove > spaceBelow;
  // Sem Math.max(240, …): o teto tem que ser o espaço que EXISTE, senão o
  // overflow-y-auto interno não adianta e o painel passa da borda.
  const available = Math.max(0, flipUp ? spaceAbove : spaceBelow);
  const maxHeight = Math.min(available, 40 * 16);

  return {
    position: 'fixed',
    ...(flipUp
      ? { bottom: `${window.innerHeight - rect.top + PANEL_GAP}px` }
      : { top: `${rect.bottom + PANEL_GAP}px` }),
    left: `${left}px`,
    width: `${width}px`,
    maxHeight: `${maxHeight}px`,
    zIndex: 60,
  };
}

// O gatilho dos dois menus vive no ChatListHeader (header V3), então a âncora
// chega de fora como rect. Guardamos o último rect pra reposicionar no resize.
const externalMoreRect = ref(null);
const externalFilterRect = ref(null);

const styleFromRect = rect =>
  rect ? computePanelStyle({ getBoundingClientRect: () => rect }) : {};

function positionMorePanel() {
  morePanelStyle.value = externalMoreRect.value
    ? styleFromRect(externalMoreRect.value)
    : computePanelStyle(moreTriggerRef.value);
}

function positionFilterPanel() {
  filterPanelStyle.value = externalFilterRect.value
    ? styleFromRect(externalFilterRect.value)
    : computePanelStyle(filterTriggerRef.value);
}

function onMorePanelResize() {
  if (isMoreOpen.value) positionMorePanel();
  if (isFilterOpen.value) positionFilterPanel();
}

const closeMorePanel = () => {
  isMoreOpen.value = false;
};

const closeFilterPanel = () => {
  isFilterOpen.value = false;
};

const normalizeAdminAssigneeScope = value =>
  ADMIN_ASSIGNEE_SCOPE_VALUES.includes(value)
    ? value
    : ADMIN_ASSIGNEE_SCOPE.ALL;

function syncDraftsFromProps() {
  draftItems.value = (props.allItems || []).map(item => ({ ...item }));
  draftIcons.value = { ...(props.iconOverrides || {}) };
  draftStatuses.value = { ...(props.statusOverrides || {}) };
  draftShowLabels.value = props.showLabels;
  draftCompact.value = props.compact;
  draftGlobalInbox.value = Array.isArray(props.globalInbox)
    ? [...props.globalInbox]
    : [];
  draftKanbanFunnel.value = props.kanbanFunnel
    ? String(props.kanbanFunnel)
    : '';
  draftKanbanStage.value = props.kanbanStage || '';
  draftKanbanPresets.value = (props.kanbanPresets || []).map(
    normalizeKanbanPreset
  );
  draftAdminAssigneeScope.value = normalizeAdminAssigneeScope(
    props.adminAssigneeScope
  );
  draftIncludeUnassignedInPendentes.value =
    normalizeIncludeUnassignedInPendentes(props.includeUnassignedInPendentes);
}

// Esc e devolução de foco: o menu do "+" virou o ÚNICO caminho pros filtros e
// pra caixa de entrada (V3), então fechar sem mouse deixou de ser detalhe.
// O @keydown.esc do painel sozinho não bastava — depois do clique no gatilho o
// foco fica NO GATILHO, fora do painel, e a tecla nunca chegava lá.
const onMorePanelKeydown = event => {
  if (event.key !== 'Escape') return;
  if (isMoreOpen.value) {
    closeMorePanel();
    moreTriggerRef.value?.querySelector('button')?.focus();
  }
  if (isFilterOpen.value) {
    closeFilterPanel();
    filterTriggerRef.value?.querySelector('button')?.focus();
  }
};

const toggleMorePanel = (rect = null) => {
  if (rect) externalMoreRect.value = rect;
  closeFilterPanel();
  if (!isMoreOpen.value) {
    syncDraftsFromProps();
    positionMorePanel();
    nextTick(() => morePanelRef.value?.focus());
  }
  isMoreOpen.value = !isMoreOpen.value;
};

const toggleFilterPanel = (rect = null) => {
  if (rect) externalFilterRect.value = rect;
  closeMorePanel();
  if (!isFilterOpen.value) {
    positionFilterPanel();
    nextTick(() => filterPanelRef.value?.focus());
  }
  isFilterOpen.value = !isFilterOpen.value;
};

const onSelectSegment = key => {
  closeMorePanel();
  closeFilterPanel();
  emit('select', key);
};

const onSelectChip = key => {
  closeMorePanel();
  closeFilterPanel();
  emit('select', key);
};

function buildLiveConfigPayload(overrides = {}) {
  return {
    order: (props.allItems || []).map(item => item.key),
    hidden: (props.allItems || [])
      .filter(item => !primaryKeySet.has(item.key) && item.hidden)
      .map(item => item.key),
    icons: { ...(props.iconOverrides || {}) },
    showLabels: props.showLabels,
    compact: props.compact,
    statuses: { ...(props.statusOverrides || {}) },
    globalInbox: Array.isArray(props.globalInbox) ? [...props.globalInbox] : [],
    kanbanFunnel: props.kanbanFunnel ? String(props.kanbanFunnel) : '',
    kanbanStage: props.kanbanStage || '',
    kanbanPresets: (props.kanbanPresets || []).map(normalizeKanbanPreset),
    adminAssigneeScope: normalizeAdminAssigneeScope(props.adminAssigneeScope),
    includeUnassignedInPendentes: normalizeIncludeUnassignedInPendentes(
      props.includeUnassignedInPendentes
    ),
    activeKey: props.activeKey,
    ...overrides,
  };
}

// SiteUp: filtros personalizados (pastas / custom views) no painel Atalhos.
const customFilterItems = computed(() =>
  (props.allItems || []).filter(
    item => item?.type === 'folder' || isFolderFilterKey(item?.key)
  )
);

function resolveCustomFilterId(item) {
  if (item?.folderId != null && item.folderId !== '') {
    return Number(item.folderId);
  }
  return Number(folderIdFromKey(item?.key));
}

function toggleCustomFilterShortcut(item) {
  if (!item?.key) return;
  const willHide = !item.hidden;
  const hidden = (props.allItems || [])
    .filter(entry => {
      if (primaryKeySet.has(entry.key)) return false;
      if (entry.key === item.key) return willHide;
      return Boolean(entry.hidden);
    })
    .map(entry => entry.key);
  emit('updateConfig', buildLiveConfigPayload({ hidden }));
}

function onEditCustomFilter(item) {
  const folderId = resolveCustomFilterId(item);
  if (!folderId) return;
  closeMorePanel();
  emit('editCustomFilter', folderId);
}

function onDeleteCustomFilter(item) {
  const folderId = resolveCustomFilterId(item);
  if (!folderId) return;
  closeMorePanel();
  emit('deleteCustomFilter', folderId);
}

const liveAdminAssigneeScope = computed(() =>
  normalizeAdminAssigneeScope(props.adminAssigneeScope)
);

const isAdminAllScope = computed(
  () => liveAdminAssigneeScope.value === ADMIN_ASSIGNEE_SCOPE.ALL
);

const isAdminMineScope = computed(
  () =>
    liveAdminAssigneeScope.value === ADMIN_ASSIGNEE_SCOPE.MINE_AND_UNASSIGNED
);

function setLiveAdminAssigneeScope(scope) {
  const next = normalizeAdminAssigneeScope(scope);
  if (next === liveAdminAssigneeScope.value) return;
  draftAdminAssigneeScope.value = next;
  emit('updateConfig', buildLiveConfigPayload({ adminAssigneeScope: next }));
}

const toggleLiveAdminAllScope = () => {
  setLiveAdminAssigneeScope(
    isAdminAllScope.value
      ? ADMIN_ASSIGNEE_SCOPE.MINE_AND_UNASSIGNED
      : ADMIN_ASSIGNEE_SCOPE.ALL
  );
};

const toggleLiveAdminMineScope = () => {
  setLiveAdminAssigneeScope(
    isAdminMineScope.value
      ? ADMIN_ASSIGNEE_SCOPE.ALL
      : ADMIN_ASSIGNEE_SCOPE.MINE_AND_UNASSIGNED
  );
};

// ===== Funil/etapa do filtro Kanban (barra ativa + personalizador) =====
// Live mirror of the built-in Kanban scope so the next open reuses the last pick.
const liveKanbanFunnel = ref('');
const liveKanbanStage = ref('');
watch(
  () => [props.kanbanFunnel, props.kanbanStage],
  ([funnel, stage]) => {
    liveKanbanFunnel.value = funnel ? String(funnel) : '';
    liveKanbanStage.value = stage || '';
  },
  { immediate: true }
);

function onActiveKanbanFunnelChange(value) {
  const funnelId = value != null ? String(value) : '';
  // Keep a live mirror so a fast funnel→stage change does not re-emit a stale
  // props.kanbanFunnel and wipe the funnel the user just picked.
  liveKanbanFunnel.value = funnelId;
  liveKanbanStage.value = '';
  if (props.activeKey === 'kanban') {
    emit(
      'updateConfig',
      buildLiveConfigPayload({ kanbanFunnel: funnelId, kanbanStage: '' })
    );
    return;
  }
  if (!isDuplicableKanbanPresetKey(props.activeKey)) return;
  const nextPresets = (props.kanbanPresets || [])
    .map(preset => {
      const normalized = normalizeKanbanPreset(preset);
      if (!normalized || normalized.key !== props.activeKey) return normalized;
      return { ...normalized, funnelId, stageKey: '' };
    })
    .filter(Boolean);
  emit('updateConfig', buildLiveConfigPayload({ kanbanPresets: nextPresets }));
}

function onActiveKanbanStageChange(value) {
  const stageKey = value != null ? String(value) : '';
  liveKanbanStage.value = stageKey;
  if (props.activeKey === 'kanban') {
    emit(
      'updateConfig',
      buildLiveConfigPayload({
        kanbanFunnel: liveKanbanFunnel.value,
        kanbanStage: stageKey,
      })
    );
    return;
  }
  if (!isDuplicableKanbanPresetKey(props.activeKey)) return;
  const nextPresets = (props.kanbanPresets || [])
    .map(preset => {
      const normalized = normalizeKanbanPreset(preset);
      if (!normalized || normalized.key !== props.activeKey) return normalized;
      return { ...normalized, stageKey };
    })
    .filter(Boolean);
  emit('updateConfig', buildLiveConfigPayload({ kanbanPresets: nextPresets }));
}

const stagesForFunnel = funnelId => {
  const funnel = props.funnels.find(f => String(f.id) === String(funnelId));
  if (!funnel || !funnel.stages) return [];
  return Object.entries(funnel.stages)
    .map(([stageKey, stage]) => ({
      key: stageKey,
      name: stage?.name || stageKey,
      position: Number(stage?.position ?? 0),
    }))
    .sort((a, b) => a.position - b.position);
};

const activeKanbanPreset = computed(() => {
  if (!isDuplicableKanbanPresetKey(props.activeKey)) return null;
  return (props.kanbanPresets || []).find(item => item.key === props.activeKey);
});

const activeKanbanFunnelModel = computed({
  get() {
    if (props.activeKey === 'kanban') {
      return liveKanbanFunnel.value;
    }
    return activeKanbanPreset.value?.funnelId
      ? String(activeKanbanPreset.value.funnelId)
      : '';
  },
  set(value) {
    onActiveKanbanFunnelChange(value);
  },
});

const activeKanbanStageModel = computed({
  get() {
    if (props.activeKey === 'kanban') return liveKanbanStage.value;
    return activeKanbanPreset.value?.stageKey || '';
  },
  set(value) {
    onActiveKanbanStageChange(value);
  },
});

const activeKanbanStageOptions = computed(() =>
  stagesForFunnel(activeKanbanFunnelModel.value)
);

const isExternalTrigger = target =>
  Boolean(
    target?.closest?.(
      '[data-testid="chat-list-filter"],[data-testid="chat-list-shortcuts"]'
    )
  );

const onDocClickChromePanels = event => {
  // Gatilho externo tem o próprio toggle — fechar aqui faria abrir-e-fechar.
  if (isExternalTrigger(event.target)) return;
  if (isMoreOpen.value) {
    if (
      !moreTriggerRef.value?.contains(event.target) &&
      !morePanelRef.value?.contains(event.target)
    ) {
      closeMorePanel();
    }
  }
  if (isFilterOpen.value) {
    if (
      !filterTriggerRef.value?.contains(event.target) &&
      !filterPanelRef.value?.contains(event.target)
    ) {
      closeFilterPanel();
    }
  }
};

onMounted(() => {
  document.addEventListener('click', onDocClickChromePanels);
  document.addEventListener('keydown', onMorePanelKeydown);
  window.addEventListener('resize', onMorePanelResize);
  nextTick(scrollActiveSegmentIntoView);
});
onBeforeUnmount(() => {
  document.removeEventListener('click', onDocClickChromePanels);
  document.removeEventListener('keydown', onMorePanelKeydown);
  window.removeEventListener('resize', onMorePanelResize);
});

const summarizeGlobalInboxIds = ids => {
  if (!ids.length) return t('CHAT_LIST.QUICK_FILTERS.GLOBAL_INBOX.ALL');
  if (ids.length === 1) {
    const inbox = props.inboxes.find(o => String(o.id) === String(ids[0]));
    return inbox?.name || t('CHAT_LIST.QUICK_FILTERS.GLOBAL_INBOX.ONE');
  }
  return t('CHAT_LIST.QUICK_FILTERS.GLOBAL_INBOX.COUNT', {
    count: ids.length,
  });
};

// ===== Caixa de entrada GLOBAL (chrome ao vivo + draft no personalizador) =====
const liveGlobalInboxIds = computed(() =>
  Array.isArray(props.globalInbox) ? props.globalInbox : []
);

const liveGlobalInboxSummary = computed(() =>
  summarizeGlobalInboxIds(liveGlobalInboxIds.value)
);

const hasLiveInboxScope = computed(() => liveGlobalInboxIds.value.length > 0);

// D1: o que o FUNIL controla — se algo aqui está ligado, o botão acende e
// ganha o dot do mockup V3. O "+" não acende por filtro: ele guarda config.
const hasAppliedFilterScope = computed(
  () => secondaryFilterActive.value || hasLiveInboxScope.value
);

const isLiveGlobalInboxSelected = id =>
  liveGlobalInboxIds.value.map(String).includes(String(id));

const selectedGlobalInboxIds = computed(() =>
  Array.isArray(draftGlobalInbox.value) ? draftGlobalInbox.value : []
);

const isGlobalInboxSelected = id =>
  selectedGlobalInboxIds.value.map(String).includes(String(id));

// Resumo: nenhuma, uma caixa ou várias caixas (personalizador).
const globalInboxSummary = computed(() =>
  summarizeGlobalInboxIds(selectedGlobalInboxIds.value)
);

function applyLiveGlobalInbox(ids) {
  const next = ids.map(Number).filter(n => !Number.isNaN(n));
  emit('updateConfig', buildLiveConfigPayload({ globalInbox: next }));
  if (isCustomizing.value) draftGlobalInbox.value = [...next];
}

function toggleLiveGlobalInbox(inboxId) {
  const id = Number(inboxId);
  const current = liveGlobalInboxIds.value.map(Number);
  applyLiveGlobalInbox(
    current.includes(id) ? current.filter(x => x !== id) : [...current, id]
  );
}

function clearLiveGlobalInbox() {
  applyLiveGlobalInbox([]);
}

// ===== Resumo do escopo admin (usa chaves de tradução existentes) =====
const adminScopeSummary = computed(() => {
  if (
    draftAdminAssigneeScope.value === ADMIN_ASSIGNEE_SCOPE.MINE_AND_UNASSIGNED
  ) {
    return t('CHAT_LIST.QUICK_FILTERS.ADMIN_SCOPE.MINE_AND_UNASSIGNED');
  }
  return t('CHAT_LIST.QUICK_FILTERS.ADMIN_SCOPE.ALL');
});

// ===== Funil/etapa no personalizador (reusa stagesForFunnel acima) =====
const kanbanStageOptions = computed(() =>
  stagesForFunnel(draftKanbanFunnel.value)
);

const stageOptionsForPreset = preset => stagesForFunnel(preset?.funnelId);

function resolveActiveKeyForEmit() {
  const order = draftItems.value.map(item => item.key);
  const current = props.activeKey;
  if (current && order.includes(current)) return current;
  return 'unread';
}

function emitConfig() {
  emit('updateConfig', {
    order: draftItems.value.map(item => item.key),
    hidden: draftItems.value.filter(item => item.hidden).map(item => item.key),
    icons: { ...draftIcons.value },
    showLabels: draftShowLabels.value,
    compact: draftCompact.value,
    statuses: { ...draftStatuses.value },
    // SiteUp: caixa GLOBAL (array) no lugar do mapa por-filtro.
    globalInbox: [...selectedGlobalInboxIds.value],
    // SiteUp: escopo funil/etapa do built-in Kanban.
    kanbanFunnel: draftKanbanFunnel.value,
    kanbanStage: draftKanbanStage.value,
    // SiteUp v654: presets Kanban duplicáveis (só entries com key kanban-*).
    kanbanPresets: draftKanbanPresets.value.map(normalizeKanbanPreset),
    // SiteUp: escopo global exclusivo para administradores.
    adminAssigneeScope: normalizeAdminAssigneeScope(
      draftAdminAssigneeScope.value
    ),
    includeUnassignedInPendentes: normalizeIncludeUnassignedInPendentes(
      draftIncludeUnassignedInPendentes.value
    ),
    // Reset when the active preset was removed from the draft order.
    activeKey: resolveActiveKeyForEmit(),
  });
}

const liveIncludeUnassignedInPendentes = computed(() =>
  normalizeIncludeUnassignedInPendentes(props.includeUnassignedInPendentes)
);

const toggleLiveIncludeUnassignedInPendentes = () => {
  emit(
    'updateConfig',
    buildLiveConfigPayload({
      includeUnassignedInPendentes: !liveIncludeUnassignedInPendentes.value,
    })
  );
};

const setDraftIncludeUnassignedInPendentes = value => {
  draftIncludeUnassignedInPendentes.value =
    normalizeIncludeUnassignedInPendentes(value);
  emitConfig();
};

function onKanbanFunnelChange() {
  // trocar de funil invalida a etapa escolhida
  draftKanbanStage.value = '';
  emitConfig();
}

function onKanbanStageChange() {
  emitConfig();
}

function findDraftPreset(key) {
  return draftKanbanPresets.value.find(item => item.key === key);
}

function onPresetFunnelChange(key) {
  const preset = findDraftPreset(key);
  if (!preset) return;
  preset.stageKey = '';
  emitConfig();
}

function onPresetStageChange() {
  emitConfig();
}

function onPresetRename(key, event) {
  const preset = findDraftPreset(key);
  if (!preset) return;
  const name = String(event?.target?.value || '').trim();
  preset.name = name || preset.name || 'Kanban';
  // Mirror name onto draft list chip.
  const listItem = draftItems.value.find(item => item.key === key);
  if (listItem) listItem.name = preset.name;
  emitConfig();
}

function nameForKanbanVariation(funnelId, stageKey) {
  const funnel = props.funnels.find(f => String(f.id) === String(funnelId));
  const funnelName = funnel?.name || t('CHAT_LIST.QUICK_FILTERS.KANBAN');
  if (!stageKey) return funnelName;
  const stage = stagesForFunnel(funnelId).find(item => item.key === stageKey);
  if (!stage?.name) return funnelName;
  return t('CHAT_LIST.QUICK_FILTERS.KANBAN_FUNNEL_STAGE_NAME', {
    funnel: funnelName,
    stage: stage.name,
  });
}

function onDuplicateKanban(sourceKey) {
  // Customizer-only: duplicate an existing Kanban shortcut into a new preset.
  let source;
  if (sourceKey === 'kanban') {
    source = {
      name: t('CHAT_LIST.QUICK_FILTERS.KANBAN'),
      icon: 'i-ph-kanban',
      funnelId: props.kanbanFunnel ? String(props.kanbanFunnel) : '',
      stageKey: props.kanbanStage || '',
    };
  } else {
    source =
      findDraftPreset(sourceKey) ||
      (props.kanbanPresets || []).find(item => item.key === sourceKey);
  }
  if (!source && sourceKey !== 'kanban') return;

  const copy = duplicateKanbanPreset(source, {
    name: nameForKanbanVariation(
      source?.funnelId || '',
      source?.stageKey || ''
    ),
  });

  draftKanbanPresets.value = [...draftKanbanPresets.value, copy];
  draftItems.value = [
    ...draftItems.value,
    {
      key: copy.key,
      type: 'kanban_preset',
      icon: copy.icon,
      name: copy.name,
      hidden: true,
      funnelId: copy.funnelId,
      stageKey: copy.stageKey,
      assigneeType: 'kanban',
    },
  ];
  emitConfig();
}

function onAddKanbanPreset() {
  const preset = createKanbanPreset({
    name: t('CHAT_LIST.QUICK_FILTERS.KANBAN_PRESET_NEW'),
  });
  draftKanbanPresets.value = [...draftKanbanPresets.value, preset];
  draftItems.value = [
    ...draftItems.value,
    {
      key: preset.key,
      type: 'kanban_preset',
      icon: preset.icon,
      name: preset.name,
      hidden: true,
      funnelId: '',
      stageKey: '',
      assigneeType: 'kanban',
    },
  ];
  emitConfig();
}

function onRemoveKanbanPreset(key) {
  if (!isDuplicableKanbanPresetKey(key)) return;
  draftKanbanPresets.value = draftKanbanPresets.value.filter(
    item => item.key !== key
  );
  draftItems.value = draftItems.value.filter(item => item.key !== key);
  emitConfig();
}

function openCustomizer() {
  closeMorePanel();
  closeFilterPanel();
  syncDraftsFromProps();
  // Concept C: cartões colapsados por padrão.
  isInboxExpanded.value = false;
  isAdminExpanded.value = false;
  isPendentesQueueExpanded.value = false;
  isCustomizing.value = true;
}

function closeCustomizer() {
  isCustomizing.value = false;
}

// SiteUp: abre o construtor de filtro avançado (substitui o antigo ícone de funil
// do cabeçalho). Fecha o personalizador antes pra o modal abrir sozinho na tela.
function onCreateFilter() {
  closeMorePanel();
  closeFilterPanel();
  closeCustomizer();
  emit('createFilter');
}

function onDragEnd() {
  emitConfig();
}

function onToggleVisibility(item) {
  item.hidden = !item.hidden;
  emitConfig();
}

// Multi-select da caixa GLOBAL: marca/desmarca uma caixa. Vazio = todas.
function toggleGlobalInbox(inboxId) {
  const id = Number(inboxId);
  const current = selectedGlobalInboxIds.value.map(Number);
  draftGlobalInbox.value = current.includes(id)
    ? current.filter(x => x !== id)
    : [...current, id];
  emitConfig();
}

// "Todas as caixas": limpa a seleção global.
function clearGlobalInbox() {
  draftGlobalInbox.value = [];
  emitConfig();
}

function setAdminAssigneeScope(scope) {
  draftAdminAssigneeScope.value = normalizeAdminAssigneeScope(scope);
  emitConfig();
}

defineExpose({
  openCustomizer,
  closeCustomizer,
  toggleFilterPanel,
  toggleMorePanel,
  isFilterOpen,
  isMoreOpen,
  hasAppliedFilterScope,
});
</script>

<template>
  <div
    data-testid="quick-filter-tabs"
    class="min-w-0"
    :class="isVertical ? 'flex h-full min-h-0 flex-col' : ''"
  >
    <!-- SiteUp v6: segmento primário + atalhos (ícones) + painel Atalhos. -->
    <div
      v-if="!isCustomizing || isVertical"
      class="flex w-full min-w-0 flex-col"
      :class="isVertical ? 'h-full min-h-0' : ''"
    >
      <div class="flex w-full min-w-0 flex-col gap-3">
        <!-- Segmento primário: largura de conteúdo, cresce se sobrar, rola se
             não couber. O `xl:` de fonte era viewport, então notebook com
             coluna de 340px ganhava 14.5px e cortava "Pendentes". -->
        <div
          v-if="!hideTabsRow"
          data-testid="quick-filter-segment"
          role="tablist"
          class="no-scrollbar flex w-full min-w-0 items-stretch overflow-x-auto overscroll-x-contain scroll-px-1 touch-pan-x border-b border-n-weak [container-type:inline-size]"
          :class="CONVERSATION_COLUMN_INSET_CLASS"
        >
          <button
            v-for="item in segmentItems"
            :key="item.key"
            :ref="el => setSegmentTabRef(item.key, el)"
            type="button"
            role="tab"
            :data-quick-filter-key="item.key"
            :aria-label="
              item.count ? `${labelFor(item)}: ${item.count}` : labelFor(item)
            "
            :title="
              iconOnlySegment
                ? item.count
                  ? `${labelFor(item)}: ${item.count}`
                  : labelFor(item)
                : undefined
            "
            :aria-selected="item.key === activeKey"
            class="relative inline-flex min-h-11 shrink-0 grow basis-auto items-center justify-center px-[clamp(0.125rem,0.6cqi,0.25rem)] leading-tight transition-colors duration-100"
            :class="[
              iconOnlySegment
                ? 'text-[11.5px]'
                : 'text-[clamp(12px,3.6cqi,13px)]',
              item.key === activeKey
                ? 'font-bold text-n-slate-12'
                : 'font-medium text-n-slate-10 hover:text-n-slate-12',
            ]"
            @click="onSelectSegment(item.key)"
          >
            <!-- Coluna compacta (dense): ícone + número. Rótulo: nunca trunca
                 e nunca encolhe abaixo do conteúdo; o tablist é quem rola. -->
            <span
              data-testid="quick-filter-segment-motion"
              class="relative inline-flex min-h-11 items-center justify-center gap-[clamp(0.125rem,0.5cqi,0.25rem)] py-2.5 transition-[max-width,opacity] duration-200 motion-reduce:transition-none"
              :class="
                iconOnlySegment
                  ? 'max-w-[4.5rem] min-w-0 overflow-hidden'
                  : 'max-w-none shrink-0'
              "
            >
              <!-- Numero AO LADO, sem balao. O badge sobreposto tinha 26px
                   contra um icone de 18px: o contador dominava o icone e o
                   conjunto lia como quebrado, ainda mais com 3 digitos
                   (224, 557). Icone um pouco maior e numero em texto. -->
              <template v-if="iconOnlySegment">
                <Icon :icon="segmentIcon(item)" class="size-5 shrink-0" />
                <span
                  v-if="item.count"
                  data-testid="quick-filter-count"
                  class="shrink-0 text-[11px] font-semibold leading-none tabular-nums"
                  :class="
                    item.key === activeKey
                      ? 'text-n-blue-11'
                      : 'text-n-slate-10'
                  "
                  aria-hidden="true"
                >
                  {{ formatCompactNumber(item.count) }}
                </span>
              </template>
              <template v-else>
                <span class="whitespace-nowrap">{{ labelFor(item) }}</span>
                <span
                  v-if="item.count"
                  data-testid="quick-filter-count"
                  class="shrink-0 rounded-full px-1 py-px text-[11px] leading-none tabular-nums"
                  :class="
                    item.key === activeKey
                      ? 'bg-n-blue-9/15 text-n-blue-11 font-semibold'
                      : 'bg-n-alpha-2 text-n-slate-11'
                  "
                  aria-hidden="true"
                >
                  {{ formatCompactNumber(item.count) }}
                </span>
              </template>
              <span
                v-if="item.key === activeKey"
                class="pointer-events-none absolute inset-x-0 bottom-0 h-1 rounded-full bg-n-blue-9"
                aria-hidden="true"
              />
            </span>
          </button>
        </div>

        <!-- Pílulas do filtro aplicado + host dos dois menus (funil e "+").
             O filtro ativo não pode ficar invisível com a fileira de chips
             extinta. `isFilterOpen` precisa estar na condição: os painéis são
             filhos deste bloco, então sem ele o menu do funil nunca montava. -->
        <div
          v-if="
            showCustomizeButton &&
            (hasAppliedFilterScope || isMoreOpen || isFilterOpen)
          "
          class="relative flex w-full min-w-0 flex-col gap-2 pb-2 empty:hidden"
          :class="CONVERSATION_COLUMN_INSET_CLASS"
        >
          <div
            v-if="activeSecondaryItem || hasLiveInboxScope"
            class="flex min-w-0 flex-wrap items-center gap-1.5"
            data-testid="quick-filter-applied-pills"
          >
            <span
              v-if="activeSecondaryItem"
              class="inline-flex h-7 min-w-0 items-center gap-1.5 rounded-full bg-n-brand/10 pl-2.5 pr-1 text-xs font-semibold text-n-blue-11"
              data-testid="quick-filter-applied-pill"
            >
              <Icon
                :icon="chipIcon(activeSecondaryItem)"
                class="size-3.5 shrink-0"
              />
              <span class="min-w-0 truncate">
                {{ labelFor(activeSecondaryItem) }}
              </span>
              <span
                v-if="activeSecondaryItem.count"
                class="shrink-0 font-normal tabular-nums opacity-80"
              >
                {{ formatCompactNumber(activeSecondaryItem.count) }}
              </span>
              <button
                type="button"
                class="inline-flex size-5 shrink-0 items-center justify-center rounded-full hover:bg-n-alpha-2"
                :aria-label="t('CHAT_LIST.QUICK_FILTERS.CLEAR_APPLIED')"
                data-testid="quick-filter-applied-clear"
                @click="clearSecondaryFilter"
              >
                <Icon icon="i-ph-x" class="size-3.5 shrink-0" />
              </button>
            </span>

            <span
              v-if="hasLiveInboxScope"
              class="inline-flex h-7 min-w-0 items-center gap-1.5 rounded-full bg-n-brand/10 pl-2.5 pr-1 text-xs font-semibold text-n-blue-11"
              data-testid="quick-filter-inbox-pill"
            >
              <Icon icon="i-ph-tray-fill" class="size-3.5 shrink-0" />
              <span class="min-w-0 truncate">{{ liveGlobalInboxSummary }}</span>
              <button
                type="button"
                class="inline-flex size-5 shrink-0 items-center justify-center rounded-full hover:bg-n-alpha-2"
                :aria-label="t('CHAT_LIST.QUICK_FILTERS.CLEAR_INBOX_SCOPE')"
                data-testid="quick-filter-inbox-clear"
                @click="clearLiveGlobalInbox"
              >
                <Icon icon="i-ph-x" class="size-3.5 shrink-0" />
              </button>
            </span>
          </div>

          <div
            v-if="showActiveKanbanScope"
            class="flex min-w-0 items-center gap-2.5 pt-0.5"
            data-testid="active-kanban-scope"
          >
            <select
              v-model="activeKanbanFunnelModel"
              :aria-label="t('CHAT_LIST.QUICK_FILTERS.KANBAN_ALL_FUNNELS')"
              class="reset-base !m-0 !mb-0 !h-8 !w-auto min-w-0 flex-1 rounded-md border border-n-weak bg-n-slate-2 !py-1 text-xs text-n-slate-12 outline-none ltr:!pl-2 ltr:!pr-6 rtl:!pr-2 rtl:!pl-6"
              data-testid="active-kanban-funnel"
            >
              <option value="">
                {{ t('CHAT_LIST.QUICK_FILTERS.KANBAN_ALL_FUNNELS') }}
              </option>
              <option
                v-for="funnel in funnels"
                :key="funnel.id"
                :value="String(funnel.id)"
              >
                {{ funnel.name }}
              </option>
            </select>
            <select
              v-model="activeKanbanStageModel"
              :aria-label="t('CHAT_LIST.QUICK_FILTERS.KANBAN_ALL_STAGES')"
              class="reset-base !m-0 !mb-0 !h-8 !w-auto min-w-0 flex-1 rounded-md border border-n-weak bg-n-slate-2 !py-1 text-xs text-n-slate-12 outline-none disabled:opacity-50 ltr:!pl-2 ltr:!pr-6 rtl:!pr-2 rtl:!pl-6"
              :disabled="!activeKanbanFunnelModel"
              data-testid="active-kanban-stage"
            >
              <option value="">
                {{ t('CHAT_LIST.QUICK_FILTERS.KANBAN_ALL_STAGES') }}
              </option>
              <option
                v-for="stage in activeKanbanStageOptions"
                :key="stage.key"
                :value="stage.key"
              >
                {{ stage.name }}
              </option>
            </select>
          </div>

          <!-- Menu do FUNIL: só o que filtra a lista. Fechar por Esc, clique
               fora ou seleção — mesma mecânica do "+". -->
          <div
            v-if="isFilterOpen"
            id="quick-filter-filter-panel"
            ref="filterPanelRef"
            data-testid="quick-filter-filter-panel"
            role="dialog"
            tabindex="-1"
            :aria-label="t('CHAT_LIST.QUICK_FILTERS.FILTER')"
            class="flex flex-col gap-2 overflow-y-auto rounded-2xl border border-n-weak bg-n-solid-1 p-3 shadow-lg outline-none"
            :style="filterPanelStyle"
          >
            <div class="flex shrink-0 items-center justify-between gap-2">
              <p class="m-0 text-sm font-medium text-n-slate-12">
                {{ t('CHAT_LIST.QUICK_FILTERS.FILTER') }}
              </p>
              <button
                type="button"
                class="inline-flex size-9 min-h-9 min-w-9 items-center justify-center rounded-md p-2 text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12"
                :aria-label="t('CHAT_LIST.QUICK_FILTERS.DISMISS')"
                data-testid="filtros-close"
                @click="closeFilterPanel"
              >
                <Icon icon="i-ph-x" class="size-5 shrink-0" />
              </button>
            </div>

            <!-- V3 travada: os destinos com contagem (ex-fileira de ícones)
                 agora moram aqui — linha com ícone, nome e número mudo. -->
            <div
              class="shrink-0 rounded-xl border border-n-weak bg-n-slate-2/40 p-1.5"
              data-testid="more-filters-section"
            >
              <p
                class="m-0 flex items-center gap-2 px-1 pb-1 pt-0.5 text-xs font-medium uppercase tracking-wide text-n-slate-11"
              >
                <Icon
                  icon="i-ph-funnel"
                  class="size-3.5 text-n-slate-10"
                />
                {{ t('CHAT_LIST.QUICK_FILTERS.FILTERS_SECTION') }}
              </p>
              <button
                v-for="item in activeChipItems"
                :key="item.key"
                type="button"
                :data-quick-filter-key="item.key"
                :data-testid="`more-filter-${item.key}`"
                class="flex min-h-9 w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-xs transition-colors hover:bg-n-alpha-2"
                :class="
                  item.key === activeKey
                    ? 'font-semibold text-n-blue-11'
                    : 'text-n-slate-12'
                "
                :aria-pressed="item.key === activeKey"
                @click="onSelectChip(item.key)"
              >
                <Icon
                  :icon="chipIcon(item)"
                  class="size-4 shrink-0"
                  :class="
                    item.key === activeKey
                      ? 'text-n-blue-11'
                      : 'text-n-slate-11'
                  "
                />
                <span class="min-w-0 flex-1 truncate">
                  {{ labelFor(item) }}
                </span>
                <span
                  v-if="item.count"
                  data-testid="quick-filter-chip-count"
                  class="shrink-0 text-[11px] tabular-nums"
                  :class="
                    item.key === activeKey
                      ? 'text-n-blue-11'
                      : 'text-n-slate-10'
                  "
                >
                  {{ formatCompactNumber(item.count) }}
                </span>
                <Icon
                  v-if="item.key === activeKey"
                  icon="i-ph-check"
                  class="size-4 shrink-0 text-n-blue-11"
                />
              </button>

              <!-- Etiquetas MESCLADA aqui, na mesma seção dos outros filtros e
                   com texto — não como ícone solto (Lucas 2026-08-10). -->
              <div data-testid="filtros-labels-row">
                <slot name="label-filter" />
              </div>
            </div>

            <!-- Caixa de entrada global (morava num popover próprio; agora é
                 seção do mesmo menu — "os filtros e as configs do lado"). -->
            <div
              class="shrink-0 rounded-xl border border-n-weak bg-n-slate-2/40 p-2.5"
              data-testid="quick-filter-inbox-panel"
            >
              <p
                class="m-0 mb-1 flex min-w-0 items-center gap-2 text-xs font-medium uppercase tracking-wide text-n-slate-11"
              >
                <Icon icon="i-ph-tray" class="size-3.5 text-n-slate-10" />
                <span class="shrink-0">
                  {{ t('CHAT_LIST.QUICK_FILTERS.GLOBAL_INBOX.TITLE') }}
                </span>
                <span
                  class="min-w-0 flex-1 truncate text-right font-normal normal-case tracking-normal text-n-slate-10"
                >
                  {{ liveGlobalInboxSummary }}
                </span>
              </p>
              <p class="m-0 mb-1.5 text-xxs text-n-slate-9">
                {{ t('CHAT_LIST.QUICK_FILTERS.GLOBAL_INBOX.DESCRIPTION') }}
              </p>
              <div class="flex max-h-48 flex-col gap-0.5 overflow-y-auto">
                <button
                  type="button"
                  data-testid="quick-filter-inbox-all"
                  class="flex items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs hover:bg-n-alpha-2"
                  :class="
                    liveGlobalInboxIds.length
                      ? 'text-n-slate-12'
                      : 'font-medium text-n-blue-11'
                  "
                  @click="clearLiveGlobalInbox"
                >
                  <span
                    class="flex size-3.5 shrink-0 items-center justify-center rounded border"
                    :class="
                      liveGlobalInboxIds.length
                        ? 'border-n-slate-6'
                        : 'border-n-brand bg-n-brand'
                    "
                  >
                    <Icon
                      v-if="!liveGlobalInboxIds.length"
                      icon="i-ph-check"
                      class="size-3 text-white"
                    />
                  </span>
                  <span class="flex-1">
                    {{ t('CHAT_LIST.QUICK_FILTERS.GLOBAL_INBOX.ALL') }}
                  </span>
                </button>
                <button
                  v-for="option in inboxes"
                  :key="option.id"
                  type="button"
                  :data-testid="`quick-filter-inbox-option-${option.id}`"
                  class="flex items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs hover:bg-n-alpha-2"
                  :class="
                    isLiveGlobalInboxSelected(option.id)
                      ? 'font-medium text-n-blue-11'
                      : 'text-n-slate-12'
                  "
                  @click="toggleLiveGlobalInbox(option.id)"
                >
                  <span
                    class="flex size-3.5 shrink-0 items-center justify-center rounded border"
                    :class="
                      isLiveGlobalInboxSelected(option.id)
                        ? 'border-n-brand bg-n-brand'
                        : 'border-n-slate-6'
                    "
                  >
                    <Icon
                      v-if="isLiveGlobalInboxSelected(option.id)"
                      icon="i-ph-check"
                      class="size-3 text-white"
                    />
                  </span>
                  <span class="min-w-0 flex-1 truncate">{{ option.name }}</span>
                </button>
              </div>
            </div>
          </div>

          <div
            v-if="isMoreOpen"
            id="quick-filter-more-panel"
            ref="morePanelRef"
            data-testid="quick-filter-more-panel"
            role="dialog"
            tabindex="-1"
            :aria-label="t('CHAT_LIST.QUICK_FILTERS.SHORTCUTS')"
            class="flex flex-col gap-2 overflow-y-auto rounded-2xl border border-n-weak bg-n-solid-1 p-3 shadow-lg outline-none"
            :style="morePanelStyle"
          >
            <div class="flex shrink-0 items-center justify-between gap-2">
              <p class="m-0 text-sm font-medium text-n-slate-12">
                {{ t('CHAT_LIST.QUICK_FILTERS.SHORTCUTS') }}
              </p>
              <button
                type="button"
                class="inline-flex size-9 min-h-9 min-w-9 items-center justify-center rounded-md p-2 text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12"
                :aria-label="t('CHAT_LIST.QUICK_FILTERS.DISMISS')"
                data-testid="atalhos-close"
                @click="closeMorePanel"
              >
                <Icon icon="i-ph-x" class="size-5 shrink-0" />
              </button>
            </div>

            <!-- Ordenação: os 7 modos vivem no ConversationSortSelector, que o
                 ChatList injeta aqui e que persiste no uiSettings do usuário. -->
            <div
              class="shrink-0 rounded-xl border border-n-weak bg-n-slate-2/40 p-2.5"
              data-testid="atalhos-sort-section"
            >
              <p
                class="m-0 mb-1 flex min-w-0 items-center gap-2 text-xs font-medium uppercase tracking-wide text-n-slate-11"
              >
                <Icon icon="i-ph-swap" class="size-3.5 text-n-slate-10" />
                <span class="shrink-0">
                  {{ t('CHAT_LIST.HEADER_FILTERS.SORT') }}
                </span>
              </p>
              <slot name="sort-modes" />
            </div>

            <div
              class="shrink-0 rounded-xl border border-n-weak bg-n-slate-2/40 p-2.5"
              data-testid="atalhos-groups-section"
            >
              <p
                class="m-0 mb-2 flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-n-slate-11"
              >
                <Icon icon="i-ph-users" class="size-3.5 text-n-slate-10" />
                {{ t('CHAT_LIST.HEADER_FILTERS.GROUPS') }}
              </p>
              <div class="flex items-center justify-between gap-3 py-1.5">
                <span
                  class="flex min-w-0 items-center gap-2 text-xs text-n-slate-12"
                >
                  <Icon
                    icon="i-ph-users"
                    class="size-4 shrink-0 text-n-slate-11"
                  />
                  <span class="truncate">{{
                    t('CHAT_LIST.QUICK_FILTERS.GROUPS_ALL')
                  }}</span>
                </span>
                <ToggleSwitch
                  data-testid="atalhos-filter-groups-visible"
                  :model-value="groupVisible"
                  @change="toggleGroupVisibility"
                />
              </div>
            </div>

            <div
              class="shrink-0 rounded-xl border border-n-weak bg-n-slate-2/40 p-2.5"
              data-testid="atalhos-pendentes-queue-section"
            >
              <p
                class="m-0 mb-2 flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-n-slate-11"
              >
                <Icon icon="i-ph-tray" class="size-3.5 text-n-slate-10" />
                {{ t('CHAT_LIST.QUICK_FILTERS.PENDENTES_QUEUE.TITLE') }}
              </p>
              <p class="m-0 mb-2 text-[11px] leading-snug text-n-slate-10">
                {{ t('CHAT_LIST.QUICK_FILTERS.PENDENTES_QUEUE.DESCRIPTION') }}
              </p>
              <div class="flex items-center justify-between gap-3 py-1.5">
                <span
                  class="flex min-w-0 items-center gap-2 text-xs text-n-slate-12"
                >
                  <Icon
                    icon="i-ph-circle"
                    class="size-4 shrink-0 text-n-slate-11"
                  />
                  <span class="truncate">{{
                    t('CHAT_LIST.QUICK_FILTERS.PENDENTES_QUEUE.SHOW_UNASSIGNED')
                  }}</span>
                </span>
                <ToggleSwitch
                  data-testid="atalhos-pendentes-show-unassigned"
                  :model-value="liveIncludeUnassignedInPendentes"
                  @change="toggleLiveIncludeUnassignedInPendentes"
                />
              </div>
            </div>

            <div
              v-if="canConfigureAdminAssigneeScope"
              class="shrink-0 rounded-xl border border-n-weak bg-n-slate-2/40 p-2.5"
              data-testid="atalhos-admin-scope-section"
            >
              <p
                class="m-0 mb-2 flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-n-slate-11"
              >
                <Icon
                  icon="i-ph-user-gear"
                  class="size-3.5 text-n-slate-10"
                />
                {{ t('CHAT_LIST.QUICK_FILTERS.ADMIN_SCOPE.TITLE') }}
              </p>
              <div class="flex items-center justify-between gap-3 py-1.5">
                <span
                  class="flex min-w-0 items-center gap-2 text-xs text-n-slate-12"
                >
                  <Icon
                    icon="i-ph-users"
                    class="size-4 shrink-0 text-n-slate-11"
                  />
                  <span class="truncate">{{
                    t('CHAT_LIST.QUICK_FILTERS.ADMIN_SCOPE.ALL')
                  }}</span>
                </span>
                <ToggleSwitch
                  data-testid="atalhos-admin-scope-all"
                  :model-value="isAdminAllScope"
                  @change="toggleLiveAdminAllScope"
                />
              </div>
              <div class="flex items-center justify-between gap-3 py-1.5">
                <span
                  class="flex min-w-0 items-center gap-2 text-xs text-n-slate-12"
                >
                  <Icon
                    icon="i-ph-user"
                    class="size-4 shrink-0 text-n-slate-11"
                  />
                  <span class="truncate">{{
                    t('CHAT_LIST.QUICK_FILTERS.ADMIN_SCOPE.MINE_ONLY')
                  }}</span>
                </span>
                <ToggleSwitch
                  data-testid="atalhos-admin-scope-mine"
                  :model-value="isAdminMineScope"
                  @change="toggleLiveAdminMineScope"
                />
              </div>
            </div>

            <div
              class="shrink-0 rounded-xl border border-n-weak bg-n-slate-2/40 p-2.5"
              data-testid="atalhos-custom-filters"
            >
              <p
                class="m-0 mb-2 flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-n-slate-11"
              >
                <Icon
                  icon="i-ph-funnel"
                  class="size-3.5 text-n-slate-10"
                />
                {{ t('CHAT_LIST.QUICK_FILTERS.CUSTOM_FILTERS') }}
              </p>
              <div v-if="customFilterItems.length" class="flex flex-col gap-1">
                <div
                  v-for="item in customFilterItems"
                  :key="item.key"
                  class="flex items-center gap-2 rounded-md px-1 py-1.5"
                  :data-testid="`atalhos-custom-filter-${resolveCustomFilterId(item)}`"
                  :data-catalog-key="item.key"
                >
                  <Icon
                    :icon="
                      resolveSavedIconClass(item.icon) || 'i-ph-folder'
                    "
                    class="size-4 shrink-0 text-n-slate-11"
                  />
                  <span
                    class="min-w-0 flex-1 truncate text-xs"
                    :class="item.hidden ? 'text-n-slate-10' : 'text-n-slate-12'"
                  >
                    {{ labelFor(item) }}
                  </span>
                  <ToggleSwitch
                    :data-testid="`atalhos-custom-filter-toggle-${resolveCustomFilterId(item)}`"
                    :model-value="!item.hidden"
                    :aria-label="
                      t('CHAT_LIST.QUICK_FILTERS.CUSTOM_FILTER_SHOW', {
                        name: labelFor(item),
                      })
                    "
                    @change="toggleCustomFilterShortcut(item)"
                  />
                  <button
                    type="button"
                    class="inline-flex size-11 shrink-0 items-center justify-center rounded-md text-n-slate-11 hover:bg-n-alpha-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-blue-9 hover:text-n-slate-12"
                    :aria-label="
                      t('CHAT_LIST.QUICK_FILTERS.CUSTOM_FILTER_EDIT', {
                        name: labelFor(item),
                      })
                    "
                    :data-testid="`atalhos-custom-filter-edit-${resolveCustomFilterId(item)}`"
                    @click="onEditCustomFilter(item)"
                  >
                    <Icon icon="i-ph-pencil-simple" class="size-5 shrink-0" />
                  </button>
                  <button
                    type="button"
                    class="inline-flex size-11 shrink-0 items-center justify-center rounded-md text-n-slate-11 hover:bg-n-alpha-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-blue-9 hover:text-n-ruby-11"
                    :aria-label="
                      t('CHAT_LIST.QUICK_FILTERS.CUSTOM_FILTER_DELETE', {
                        name: labelFor(item),
                      })
                    "
                    :data-testid="`atalhos-custom-filter-delete-${resolveCustomFilterId(item)}`"
                    @click="onDeleteCustomFilter(item)"
                  >
                    <Icon icon="i-ph-trash" class="size-5 shrink-0" />
                  </button>
                </div>
              </div>
              <p
                v-else
                class="m-0 text-xs text-n-slate-10"
                data-testid="atalhos-custom-filters-empty"
              >
                {{ t('CHAT_LIST.QUICK_FILTERS.CUSTOM_FILTERS_EMPTY') }}
              </p>
            </div>

            <div
              class="flex shrink-0 flex-col items-stretch gap-2 rounded-xl px-1 py-1"
              data-testid="atalhos-create-filter"
            >
              <p class="m-0 text-center text-xs text-n-slate-10">
                {{ t('CHAT_LIST.QUICK_FILTERS.CREATE_FILTER_HINT') }}
              </p>
              <button
                type="button"
                data-testid="quick-filter-create-custom-atalhos"
                class="flex w-full items-center justify-center gap-2 rounded-xl bg-n-brand px-3 py-2.5 text-sm font-medium text-white hover:bg-n-brand/90"
                @click="
                  closeMorePanel();
                  onCreateFilter();
                "
              >
                <Icon icon="i-ph-funnel" class="size-4 shrink-0" />
                <span>{{ t('CHAT_LIST.QUICK_FILTERS.CREATE_FILTER') }}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
      <slot v-if="isVertical" name="after-items" />
    </div>

    <!-- Concept C · Painel contido na coluna de conversas (alvo interno) -->
    <Teleport v-if="isCustomizing" :to="teleportTarget">
      <div
        data-testid="quick-filter-customizer"
        role="dialog"
        aria-modal="true"
        :aria-label="t('CHAT_LIST.QUICK_FILTERS.CUSTOMIZE')"
        class="pointer-events-auto absolute inset-y-0 z-10 flex w-full max-w-md flex-col border-n-weak bg-n-surface-1 shadow-xl ltr:left-0 ltr:border-r rtl:right-0 rtl:border-l"
        @keydown.esc="closeCustomizer"
      >
        <!-- Cartão-resumo: Caixa de entrada GLOBAL (colapsado por padrão) -->
        <div class="mx-3 mt-3 border rounded-xl border-n-weak bg-n-slate-2">
          <button
            type="button"
            class="flex items-center w-full gap-2 px-3 py-2.5 text-left rounded-xl hover:bg-n-alpha-2 focus:outline-none focus-visible:ring-1 focus-visible:ring-n-brand"
            :aria-expanded="isInboxExpanded"
            aria-controls="qf-inbox-panel"
            @click="isInboxExpanded = !isInboxExpanded"
          >
            <Icon
              icon="i-ph-tray"
              class="flex-shrink-0 w-4 h-4 text-n-slate-11"
            />
            <span class="text-sm font-medium text-n-slate-12">
              {{ t('CHAT_LIST.QUICK_FILTERS.GLOBAL_INBOX.TITLE') }}
            </span>
            <span class="ml-auto text-xs truncate text-n-slate-10">
              {{ globalInboxSummary }}
            </span>
            <Icon
              :icon="
                isInboxExpanded
                  ? 'i-ph-caret-up'
                  : 'i-ph-caret-down'
              "
              class="flex-shrink-0 w-4 h-4 text-n-slate-10"
            />
          </button>
          <div v-show="isInboxExpanded" id="qf-inbox-panel" class="px-3 pb-2.5">
            <p class="mb-1.5 text-xs text-n-slate-10">
              {{ t('CHAT_LIST.QUICK_FILTERS.GLOBAL_INBOX.DESCRIPTION') }}
            </p>
            <div class="flex flex-col gap-0.5 max-h-48 overflow-y-auto">
              <button
                type="button"
                class="flex items-center gap-2 px-2 py-1.5 text-xs text-left rounded-md hover:bg-n-alpha-2"
                :class="
                  selectedGlobalInboxIds.length
                    ? 'text-n-slate-12'
                    : 'text-n-blue-11 font-medium'
                "
                @click="clearGlobalInbox"
              >
                <span
                  class="flex items-center justify-center flex-shrink-0 border rounded size-3.5"
                  :class="
                    selectedGlobalInboxIds.length
                      ? 'border-n-slate-6'
                      : 'bg-n-brand border-n-brand'
                  "
                >
                  <Icon
                    v-if="!selectedGlobalInboxIds.length"
                    icon="i-ph-check"
                    class="size-3 text-white"
                  />
                </span>
                <span class="flex-1">
                  {{ t('CHAT_LIST.QUICK_FILTERS.GLOBAL_INBOX.ALL') }}
                </span>
              </button>
              <button
                v-for="option in inboxes"
                :key="option.id"
                type="button"
                class="flex items-center gap-2 px-2 py-1.5 text-xs text-left rounded-md hover:bg-n-alpha-2"
                :class="
                  isGlobalInboxSelected(option.id)
                    ? 'text-n-blue-11 font-medium'
                    : 'text-n-slate-12'
                "
                @click="toggleGlobalInbox(option.id)"
              >
                <span
                  class="flex items-center justify-center flex-shrink-0 border rounded size-3.5"
                  :class="
                    isGlobalInboxSelected(option.id)
                      ? 'bg-n-brand border-n-brand'
                      : 'border-n-slate-6'
                  "
                >
                  <Icon
                    v-if="isGlobalInboxSelected(option.id)"
                    icon="i-ph-check"
                    class="size-3 text-white"
                  />
                </span>
                <span class="flex-1 truncate">{{ option.name }}</span>
              </button>
            </div>
          </div>
        </div>

        <!-- Cartão-resumo: Fila Pendentes (colapsado por padrão) -->
        <div
          class="mx-3 mt-2 border rounded-xl border-n-weak bg-n-slate-2"
          data-testid="atalhos-pendentes-queue-customizer"
        >
          <button
            type="button"
            class="flex items-center w-full gap-2 px-3 py-2.5 text-left rounded-xl hover:bg-n-alpha-2 focus:outline-none focus-visible:ring-1 focus-visible:ring-n-brand"
            :aria-expanded="isPendentesQueueExpanded"
            aria-controls="qf-pendentes-queue-panel"
            @click="isPendentesQueueExpanded = !isPendentesQueueExpanded"
          >
            <Icon
              icon="i-ph-tray"
              class="flex-shrink-0 w-4 h-4 text-n-slate-11"
            />
            <span class="text-sm font-medium text-n-slate-12">
              {{ t('CHAT_LIST.QUICK_FILTERS.PENDENTES_QUEUE.TITLE') }}
            </span>
            <span class="ml-auto text-xs truncate text-n-slate-10">
              {{
                draftIncludeUnassignedInPendentes
                  ? t('CHAT_LIST.QUICK_FILTERS.PENDENTES_QUEUE.SHOW_UNASSIGNED')
                  : t('CHAT_LIST.QUICK_FILTERS.PENDENTES_QUEUE.HIDE_UNASSIGNED')
              }}
            </span>
            <Icon
              :icon="
                isPendentesQueueExpanded
                  ? 'i-ph-caret-up'
                  : 'i-ph-caret-down'
              "
              class="flex-shrink-0 w-4 h-4 text-n-slate-10"
            />
          </button>
          <div
            v-show="isPendentesQueueExpanded"
            id="qf-pendentes-queue-panel"
            class="px-3 pb-2.5"
          >
            <p class="mb-2 text-xs text-n-slate-10">
              {{ t('CHAT_LIST.QUICK_FILTERS.PENDENTES_QUEUE.DESCRIPTION') }}
            </p>
            <div class="flex items-center justify-between gap-3 py-1">
              <span class="text-xs text-n-slate-12">
                {{
                  t('CHAT_LIST.QUICK_FILTERS.PENDENTES_QUEUE.SHOW_UNASSIGNED')
                }}
              </span>
              <ToggleSwitch
                data-testid="atalhos-pendentes-show-unassigned-draft"
                :model-value="draftIncludeUnassignedInPendentes"
                @change="
                  setDraftIncludeUnassignedInPendentes(
                    !draftIncludeUnassignedInPendentes
                  )
                "
              />
            </div>
          </div>
        </div>

        <!-- Cartão-resumo: Escopo admin (só pra admins, colapsado por padrão) -->
        <div
          v-if="canConfigureAdminAssigneeScope"
          class="mx-3 mt-2 border rounded-xl border-n-weak bg-n-slate-2"
        >
          <button
            type="button"
            class="flex items-center w-full gap-2 px-3 py-2.5 text-left rounded-xl hover:bg-n-alpha-2 focus:outline-none focus-visible:ring-1 focus-visible:ring-n-brand"
            :aria-expanded="isAdminExpanded"
            aria-controls="qf-admin-panel"
            @click="isAdminExpanded = !isAdminExpanded"
          >
            <Icon
              icon="i-ph-user-gear"
              class="flex-shrink-0 w-4 h-4 text-n-slate-11"
            />
            <span class="text-sm font-medium text-n-slate-12">
              {{ t('CHAT_LIST.QUICK_FILTERS.ADMIN_SCOPE.TITLE') }}
            </span>
            <span class="ml-auto text-xs truncate text-n-slate-10">
              {{ adminScopeSummary }}
            </span>
            <Icon
              :icon="
                isAdminExpanded
                  ? 'i-ph-caret-up'
                  : 'i-ph-caret-down'
              "
              class="flex-shrink-0 w-4 h-4 text-n-slate-10"
            />
          </button>
          <div v-show="isAdminExpanded" id="qf-admin-panel" class="px-3 pb-2.5">
            <p class="mb-1.5 text-xs text-n-slate-10">
              {{ t('CHAT_LIST.QUICK_FILTERS.ADMIN_SCOPE.DESCRIPTION') }}
            </p>
            <div class="grid grid-cols-2 gap-1">
              <button
                type="button"
                class="flex items-center justify-center min-w-0 gap-1.5 px-2 py-1.5 text-xs font-medium rounded-md transition-colors"
                :class="
                  draftAdminAssigneeScope === ADMIN_ASSIGNEE_SCOPE.ALL
                    ? 'bg-n-brand/10 text-n-blue-11'
                    : 'text-n-slate-11 hover:bg-n-alpha-2'
                "
                @click="setAdminAssigneeScope(ADMIN_ASSIGNEE_SCOPE.ALL)"
              >
                <Icon icon="i-ph-users" class="flex-shrink-0 size-3.5" />
                <span class="truncate">
                  {{ t('CHAT_LIST.QUICK_FILTERS.ADMIN_SCOPE.ALL') }}
                </span>
              </button>
              <button
                type="button"
                class="flex items-center justify-center min-w-0 gap-1.5 px-2 py-1.5 text-xs font-medium rounded-md transition-colors"
                :class="
                  draftAdminAssigneeScope ===
                  ADMIN_ASSIGNEE_SCOPE.MINE_AND_UNASSIGNED
                    ? 'bg-n-brand/10 text-n-blue-11'
                    : 'text-n-slate-11 hover:bg-n-alpha-2'
                "
                @click="
                  setAdminAssigneeScope(
                    ADMIN_ASSIGNEE_SCOPE.MINE_AND_UNASSIGNED
                  )
                "
              >
                <Icon
                  icon="i-ph-user-check"
                  class="flex-shrink-0 size-3.5"
                />
                <span class="truncate">
                  {{
                    t('CHAT_LIST.QUICK_FILTERS.ADMIN_SCOPE.MINE_AND_UNASSIGNED')
                  }}
                </span>
              </button>
            </div>
          </div>
        </div>

        <!-- Região de rolagem única: lista arrastável -->
        <div class="flex-1 overflow-y-auto px-3 pt-2 pb-2 max-h-[50vh]">
          <Draggable
            v-model="draftItems"
            item-key="key"
            handle=".qf-drag-handle"
            @end="onDragEnd"
          >
            <template #item="{ element: item }">
              <div class="border-b border-n-alpha-2 last:border-b-0">
                <div class="flex items-center gap-2 py-2.5">
                  <Icon
                    icon="i-woot-drag-indicator"
                    class="flex-shrink-0 w-5 h-5 cursor-move text-n-slate-10 qf-drag-handle"
                  />
                  <Icon
                    :icon="resolveSavedIconClass(item.icon)"
                    class="flex-shrink-0 w-[22px] h-[22px] text-n-slate-11"
                  />
                  <input
                    v-if="isDuplicableKanbanPresetKey(item.key)"
                    type="text"
                    class="reset-base !m-0 !mb-0 !h-8 min-w-0 flex-1 rounded-md border border-n-weak bg-n-slate-2 px-2 text-sm text-n-slate-12 outline-none"
                    :value="labelFor(item)"
                    :aria-label="
                      t('CHAT_LIST.QUICK_FILTERS.KANBAN_PRESET_RENAME')
                    "
                    @change="onPresetRename(item.key, $event)"
                  />
                  <span
                    v-else
                    class="flex-1 text-sm truncate"
                    :class="item.hidden ? 'text-n-slate-10' : 'text-n-slate-12'"
                  >
                    {{ labelFor(item) }}
                  </span>
                  <button
                    v-if="isKanbanPresetKey(item.key)"
                    type="button"
                    class="inline-flex size-7 items-center justify-center rounded-md text-n-slate-11 hover:bg-n-alpha-2"
                    :aria-label="
                      t('CHAT_LIST.QUICK_FILTERS.KANBAN_PRESET_DUPLICATE')
                    "
                    data-testid="qf-duplicate-kanban"
                    @click="onDuplicateKanban(item.key)"
                  >
                    <Icon icon="i-ph-copy" class="size-4 shrink-0" />
                  </button>
                  <button
                    v-if="isDuplicableKanbanPresetKey(item.key)"
                    type="button"
                    class="inline-flex size-7 items-center justify-center rounded-md text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-ruby-11"
                    :aria-label="
                      t('CHAT_LIST.QUICK_FILTERS.KANBAN_PRESET_DELETE')
                    "
                    data-testid="qf-delete-kanban"
                    @click="onRemoveKanbanPreset(item.key)"
                  >
                    <Icon icon="i-ph-trash" class="size-4 shrink-0" />
                  </button>
                  <ToggleSwitch
                    :model-value="!item.hidden"
                    :aria-label="t('SWITCH.TOGGLE')"
                    @change="onToggleVisibility(item)"
                  />
                </div>
                <!-- SiteUp: built-in Kanban — seletor de funil + etapa -->
                <div
                  v-if="item.key === 'kanban'"
                  class="flex min-w-0 items-center gap-2 pb-2.5 ltr:pl-7 rtl:pr-7"
                >
                  <select
                    v-model="draftKanbanFunnel"
                    :aria-label="
                      t('CHAT_LIST.QUICK_FILTERS.KANBAN_ALL_FUNNELS')
                    "
                    class="reset-base !m-0 !mb-0 !h-8 !w-auto min-w-0 flex-1 rounded-md border border-n-weak bg-n-slate-2 !py-1 text-xs text-n-slate-12 outline-none ltr:!pl-2 ltr:!pr-6 rtl:!pr-2 rtl:!pl-6"
                    @change="onKanbanFunnelChange"
                  >
                    <option value="">
                      {{ t('CHAT_LIST.QUICK_FILTERS.KANBAN_ALL_FUNNELS') }}
                    </option>
                    <option
                      v-for="funnel in funnels"
                      :key="funnel.id"
                      :value="String(funnel.id)"
                    >
                      {{ funnel.name }}
                    </option>
                  </select>
                  <select
                    v-model="draftKanbanStage"
                    :aria-label="t('CHAT_LIST.QUICK_FILTERS.KANBAN_ALL_STAGES')"
                    class="reset-base !m-0 !mb-0 !h-8 !w-auto min-w-0 flex-1 rounded-md border border-n-weak bg-n-slate-2 !py-1 text-xs text-n-slate-12 outline-none disabled:opacity-50 ltr:!pl-2 ltr:!pr-6 rtl:!pr-2 rtl:!pl-6"
                    :disabled="!draftKanbanFunnel"
                    @change="onKanbanStageChange"
                  >
                    <option value="">
                      {{ t('CHAT_LIST.QUICK_FILTERS.KANBAN_ALL_STAGES') }}
                    </option>
                    <option
                      v-for="stage in kanbanStageOptions"
                      :key="stage.key"
                      :value="stage.key"
                    >
                      {{ stage.name }}
                    </option>
                  </select>
                </div>
                <!-- SiteUp v654: preset Kanban duplicável — funil (+ etapa opcional) -->
                <div
                  v-else-if="
                    isDuplicableKanbanPresetKey(item.key) &&
                    findDraftPreset(item.key)
                  "
                  class="flex min-w-0 items-center gap-2 pb-2.5 ltr:pl-7 rtl:pr-7"
                >
                  <select
                    v-model="findDraftPreset(item.key).funnelId"
                    :aria-label="
                      t('CHAT_LIST.QUICK_FILTERS.KANBAN_ALL_FUNNELS')
                    "
                    class="reset-base !m-0 !mb-0 !h-8 !w-auto min-w-0 flex-1 rounded-md border border-n-weak bg-n-slate-2 !py-1 text-xs text-n-slate-12 outline-none ltr:!pl-2 ltr:!pr-6 rtl:!pr-2 rtl:!pl-6"
                    @change="onPresetFunnelChange(item.key)"
                  >
                    <option value="">
                      {{ t('CHAT_LIST.QUICK_FILTERS.KANBAN_ALL_FUNNELS') }}
                    </option>
                    <option
                      v-for="funnel in funnels"
                      :key="funnel.id"
                      :value="String(funnel.id)"
                    >
                      {{ funnel.name }}
                    </option>
                  </select>
                  <select
                    v-model="findDraftPreset(item.key).stageKey"
                    :aria-label="t('CHAT_LIST.QUICK_FILTERS.KANBAN_ALL_STAGES')"
                    class="reset-base !m-0 !mb-0 !h-8 !w-auto min-w-0 flex-1 rounded-md border border-n-weak bg-n-slate-2 !py-1 text-xs text-n-slate-12 outline-none disabled:opacity-50 ltr:!pl-2 ltr:!pr-6 rtl:!pr-2 rtl:!pl-6"
                    :disabled="!findDraftPreset(item.key).funnelId"
                    @change="onPresetStageChange"
                  >
                    <option value="">
                      {{ t('CHAT_LIST.QUICK_FILTERS.KANBAN_ALL_STAGES') }}
                    </option>
                    <option
                      v-for="stage in stageOptionsForPreset(
                        findDraftPreset(item.key)
                      )"
                      :key="stage.key"
                      :value="stage.key"
                    >
                      {{ stage.name }}
                    </option>
                  </select>
                </div>
              </div>
            </template>
          </Draggable>
          <button
            type="button"
            class="mt-2 flex w-full items-center justify-center gap-2 rounded-xl border border-dashed border-n-weak px-3 py-2 text-xs font-medium text-n-blue-11 hover:bg-n-brand/10"
            data-testid="qf-add-kanban-preset"
            @click="onAddKanbanPreset"
          >
            <Icon icon="i-ph-plus" class="size-3.5 shrink-0" />
            {{ t('CHAT_LIST.QUICK_FILTERS.KANBAN_PRESET_ADD') }}
          </button>
        </div>

        <!-- Botão Concluir fixo no rodapé (sticky full-width) -->
        <div
          class="sticky bottom-0 px-3 py-2 border-t border-n-weak bg-n-surface-1"
        >
          <button
            type="button"
            class="flex items-center justify-center w-full gap-2 px-3 py-2.5 text-sm font-medium transition-colors rounded-xl text-white bg-n-brand hover:bg-n-brand/90"
            @click="closeCustomizer"
          >
            {{ t('CHAT_LIST.QUICK_FILTERS.CUSTOMIZE_DONE') }}
          </button>
        </div>
      </div>
    </Teleport>
  </div>
</template>
